import { sortEvents } from '@wr/shared';
import { get } from '../../api.js';
import type { LocalRecord, LocalStore } from '../../offline/store.js';
import { isUnreachable } from '../people/data.js';

/** An event of the list, with the whole team's numbers when the portal answers. */
export type EventRow = LocalRecord & {
  contacts?: number;
  leads?: number;
  /** Nov 2, 2026: what keeps it from being deleted (from the portal only, not offline). */
  captures?: { contacts: number; leads: number; notes: number; receipts: number };
};

/** "2 contacts, 1 lead, 3 notes and 1 receipt": what is captured for an event. */
export function capturesText(c: NonNullable<EventRow['captures']>): string {
  const parts = (
    [
      [c.contacts, 'contact', 'contacts'],
      [c.leads, 'lead', 'leads'],
      [c.notes, 'note', 'notes'],
      [c.receipts, 'receipt', 'receipts'],
    ] as const
  )
    .filter(([n]) => n > 0)
    .map(([n, one, many]) => `${n} ${n === 1 ? one : many}`);
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}` : parts.join('');
}

/**
 * The events (brief section 7.1): from the portal with their numbers of
 * contacts and leads, with this device's changes not sent yet; without
 * network, the events this device keeps (no numbers).
 */
export async function listEvents(
  store: LocalStore | null,
): Promise<{ items: EventRow[]; offline: boolean }> {
  let remote: EventRow[] | null = null;
  try {
    remote = (await get<{ items: EventRow[] }>('/events')).items;
  } catch (error) {
    if (!store || !isUnreachable(error)) throw error;
  }
  const local = store ? await store.listRecords('event') : [];
  const waiting = store
    ? (await store.outbox()).map((i) => i.op).filter((op) => op.entity === 'event')
    : [];
  const byId = new Map<string, EventRow>();
  for (const e of remote ?? local) if (!e.deleted) byId.set(e.id, e);
  // What this device saved and has not sent yet shows at once.
  for (const op of waiting) {
    if (op.entity !== 'event' || !('id' in op)) continue;
    if (op.action === 'delete') byId.delete(op.id);
    else {
      const e = local.find((r) => r.id === op.id);
      if (e && !e.deleted) byId.set(op.id, { ...byId.get(op.id), ...e });
    }
  }
  return { items: sortEvents([...byId.values()]), offline: remote === null };
}
